/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface DeclineCategoryProps {
  id: string;
  name: string;
  defaultDeclineReasonId: string | null;
  isVisibleInOps: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class DeclineCategoryRecord extends Record<DeclineCategoryProps>({
  id: null as any,
  name: null as any,
  defaultDeclineReasonId: null as any,
  isVisibleInOps: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    defaultDeclineReasonId: string | null;
    isVisibleInOps: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): DeclineCategoryRecord {
    if (json == null) {
      throw new Error('DeclineCategoryRecord.fromJS: parameter cannot be null');
    }
    return new DeclineCategoryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'DeclineCategoryJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'DeclineCategoryJson.name'),
      defaultDeclineReasonId: json.defaultDeclineReasonId,
      isVisibleInOps: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isVisibleInOps', false),
        'DeclineCategoryJson.isVisibleInOps',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    defaultDeclineReasonId: string | null;
    isVisibleInOps: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [DeclineCategoryRecord, Error[]] {
    if (json == null) {
      throw new Error('DeclineCategoryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new DeclineCategoryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'DeclineCategoryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'DeclineCategoryJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      defaultDeclineReasonId: tryOrDefault(
        () => json.defaultDeclineReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isVisibleInOps: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isVisibleInOps', false),
            'DeclineCategoryJson.isVisibleInOps',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: DeclineCategoryProps) {
    super(props);
    if (props == null) {
      throw new Error('DeclineCategoryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class DeclineCategoryViewModelBase implements DeclineCategoryProps {
  protected readonly _props: DeclineCategoryProps;
  private _json?: DeclineCategoryProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get defaultDeclineReasonId(): string | null {
    return this._props.defaultDeclineReasonId;
  }
  get isVisibleInOps(): boolean {
    return this._props.isVisibleInOps;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: DeclineCategoryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        defaultDeclineReasonId: this._props.defaultDeclineReasonId,
        isVisibleInOps: this._props.isVisibleInOps,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllDeclineCategoryEntities = (state: AppState) =>
  state.entityStores.declineCategory.entities;

export const selectDeclineCategoryById = (id: string) => (state: AppState) =>
  state.entityStores.declineCategory.entities.get(id);

export const selectDeclineCategoryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.declineCategory.meta.get(id);

export const selectDeclineCategoryAndMetadataById = (id: string) =>
  createSelector(
    selectDeclineCategoryById(id),
    selectDeclineCategoryMetadataById(id),
    (declineCategory, meta) => ({ declineCategory, meta }),
  );

export function selectDeclineCategoryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllDeclineCategoryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, DeclineCategoryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectDeclineCategoryEntitiesByName(name: string) {
  return createSelector(selectAllDeclineCategoryEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectDeclineCategoryEntitiesByDefaultDeclineReasonId(
  defaultDeclineReasonId: string,
) {
  return createSelector(selectAllDeclineCategoryEntities, (entities) =>
    entities.filter((e) => e.defaultDeclineReasonId === defaultDeclineReasonId).valueSeq(),
  );
}

export function selectDeclineCategoryEntitiesByIsVisibleInOps(isVisibleInOps: boolean) {
  return createSelector(selectAllDeclineCategoryEntities, (entities) =>
    entities.filter((e) => e.isVisibleInOps === isVisibleInOps).valueSeq(),
  );
}
//#endregion Selectors
